import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCart, TrendingUp, Package, BarChart3, Building2,
  CalendarDays, Trophy, Lightbulb, Snowflake, Layers,
} from 'lucide-react';
import {
  fmt, fmtMoney, monthLabel, SummaryCard, HorizontalBar,
  MonthlyChart, TopProductsList,
} from '@/components/stats/StatsShared';

const STATUS_COLORS = {
  'Новый': 'bg-blue-100 text-blue-700',
  'В обработке': 'bg-yellow-100 text-yellow-700',
  'Завершен': 'bg-green-100 text-green-700',
  'Отменен': 'bg-red-100 text-red-700',
};

const STATUS_BAR_COLORS = {
  'Новый': 'bg-blue-500',
  'В обработке': 'bg-yellow-400',
  'Завершен': 'bg-green-500',
  'Отменен': 'bg-red-400',
};

export default function SupplierStatsPanel({ data }) {
  if (!data) return null;

  const {
    summary, status_distribution, by_supplier,
    top_products_by_qty, top_products_by_cost,
    monthly_purchases, supply_status,
    seasonal_products, order_recommendations, by_category,
  } = data;

  const maxSupplierAmount = Math.max(...(by_supplier || []).map((s) => s.amount), 1);
  const maxStatusCount = Math.max(...(status_distribution || []).map((s) => s.count), 1);
  const maxCategoryQty = Math.max(...(by_category || []).map((c) => c.total_qty), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <SummaryCard icon={ShoppingCart} label="Заказов поставщикам" value={fmt(summary.total_orders)} color="bg-blue-500" />
        <SummaryCard icon={TrendingUp} label="Сумма закупок" value={fmtMoney(summary.total_purchase_amount)} color="bg-green-500" />
        <SummaryCard icon={BarChart3} label="Средний заказ" value={fmtMoney(summary.avg_order)} color="bg-indigo-500" />
        <SummaryCard icon={Building2} label="Поставщиков" value={fmt(summary.active_suppliers)} color="bg-purple-500" />
        <SummaryCard icon={Package} label="Позиций заказано" value={fmt(summary.total_items_ordered)} color="bg-orange-500" />
      </div>

      <Tabs defaultValue="products">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="products"><Trophy className="w-4 h-4 mr-1.5" />Топ товаров</TabsTrigger>
          <TabsTrigger value="suppliers"><Building2 className="w-4 h-4 mr-1.5" />Поставщики</TabsTrigger>
          <TabsTrigger value="seasonal"><Snowflake className="w-4 h-4 mr-1.5" />Сезонность</TabsTrigger>
          <TabsTrigger value="recommendations"><Lightbulb className="w-4 h-4 mr-1.5" />Рекомендации</TabsTrigger>
          <TabsTrigger value="dynamics"><CalendarDays className="w-4 h-4 mr-1.5" />Динамика</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">Чаще всего закупаем (по кол-ву)</CardTitle>
              </CardHeader>
              <CardContent>
                <TopProductsList
                  items={top_products_by_qty}
                  valueKey="total_qty"
                  valueLabel={(p) => `${fmt(p.total_qty)} шт`}
                  barColor="bg-blue-500"
                />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">Топ по сумме закупки</CardTitle>
              </CardHeader>
              <CardContent>
                <TopProductsList
                  items={top_products_by_cost}
                  valueKey="total_cost"
                  valueLabel={(p) => fmtMoney(p.total_cost)}
                  barColor="bg-green-500"
                />
              </CardContent>
            </Card>
          </div>

          {(by_category || []).length > 0 && (
            <Card className="border-0 shadow-sm mt-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Layers className="w-4 h-4" /> По категориям
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {by_category.map((cat) => (
                  <div key={cat.category}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{cat.category}</span>
                      <div className="flex gap-3 text-sm">
                        <span className="text-gray-500">{fmt(cat.total_qty)} шт</span>
                        <span className="font-semibold text-green-700">{fmtMoney(cat.total_cost)}</span>
                      </div>
                    </div>
                    <HorizontalBar value={cat.total_qty} max={maxCategoryQty} color="bg-teal-500" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="suppliers">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">Закупки по поставщикам</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(by_supplier || []).length === 0 && <p className="text-gray-400 text-sm">Нет данных</p>}
                {(by_supplier || []).map((s, i) => (
                  <div key={s.supplier || i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{s.supplier}</span>
                      <div className="flex gap-3 text-sm">
                        <span className="text-gray-500">{fmt(s.orders)} зак.</span>
                        <span className="font-semibold text-green-700">{fmtMoney(s.amount)}</span>
                      </div>
                    </div>
                    <HorizontalBar value={s.amount} max={maxSupplierAmount} color="bg-indigo-500" />
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-gray-800">Статусы заказов</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(status_distribution || []).map((s, i) => (
                    <div key={s.status || i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-600'}`}>
                          {s.status}
                        </span>
                        <div className="flex gap-3 text-sm">
                          <span className="text-gray-500">{fmt(s.count)} шт</span>
                          <span className="font-semibold text-gray-700">{fmtMoney(s.amount)}</span>
                        </div>
                      </div>
                      <HorizontalBar value={s.count} max={maxStatusCount} color={STATUS_BAR_COLORS[s.status] || 'bg-gray-400'} />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {(supply_status || []).length > 0 && (
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-gray-800">Статусы поставок</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {supply_status.map((s, i) => (
                      <div key={s.status || i} className="flex items-center justify-between text-sm">
                        <Badge variant="outline">{s.status}</Badge>
                        <div className="flex gap-3">
                          <span className="text-gray-500">{fmt(s.count)} поставок</span>
                          <span className="font-semibold">{fmtMoney(s.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seasonal">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-800">Сезонный спрос в текущем месяце</CardTitle>
              <p className="text-xs text-gray-400">Товары, спрос на которые в этом месяце заметно выше среднего</p>
            </CardHeader>
            <CardContent>
              {(seasonal_products || []).length === 0 && (
                <p className="text-gray-400 text-sm py-4">Пока нет выраженной сезонности — данных недостаточно</p>
              )}
              <div className="space-y-4">
                {(seasonal_products || []).map((p) => (
                  <div key={p.product_id} className="border-b border-gray-50 pb-4 last:border-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{p.product_name}</p>
                        <p className="text-xs text-gray-400">{p.product_code}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-orange-600">×{p.peak_ratio} к среднему</p>
                        <p className="text-xs text-gray-500">{p.current_month_qty} шт сейчас / ~{p.avg_monthly_qty} в среднем</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommendations">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-800">Рекомендации к заказу</CardTitle>
              <p className="text-xs text-gray-400">Товары с регулярным спросом, которые стоит заказать у поставщика</p>
            </CardHeader>
            <CardContent>
              {(order_recommendations || []).length === 0 && (
                <p className="text-gray-400 text-sm py-4">Все популярные позиции недавно заказывались</p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-gray-500 font-medium">Товар</th>
                      <th className="text-right py-2 text-gray-500 font-medium">Ср./мес</th>
                      <th className="text-right py-2 text-gray-500 font-medium">За 30 дн</th>
                      <th className="text-right py-2 text-gray-500 font-medium">Рекоменд.</th>
                      <th className="text-left py-2 text-gray-500 font-medium pl-4">Причина</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order_recommendations || []).map((r) => (
                      <tr key={r.product_id} className="border-b border-gray-50">
                        <td className="py-2">
                          <p className="font-medium text-gray-900">{r.product_name}</p>
                          <p className="text-xs text-gray-400">{r.product_code}</p>
                        </td>
                        <td className="py-2 text-right text-gray-600">{r.avg_monthly_qty}</td>
                        <td className="py-2 text-right text-gray-600">{r.last_30_days_qty}</td>
                        <td className="py-2 text-right font-semibold text-blue-700">{r.suggested_qty} шт</td>
                        <td className="py-2 pl-4 text-xs text-gray-500">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dynamics">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-1">
              <CardTitle className="text-base font-semibold text-gray-800">Закупки по месяцам</CardTitle>
              <p className="text-xs text-gray-400">Последние 12 месяцев</p>
            </CardHeader>
            <CardContent>
              <MonthlyChart data={monthly_purchases} valueKey="amount" label="amount" color="#6366f1" />
              {monthly_purchases?.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-1 text-gray-400 font-medium">Месяц</th>
                        <th className="text-right py-1 text-gray-400 font-medium">Заказов</th>
                        <th className="text-right py-1 text-gray-400 font-medium">Сумма закупки</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...monthly_purchases].reverse().map((m) => (
                        <tr key={m.month} className="border-b border-gray-50">
                          <td className="py-1 text-gray-700">{monthLabel(m.month)}</td>
                          <td className="py-1 text-right text-gray-600">{fmt(m.orders)}</td>
                          <td className="py-1 text-right font-medium text-indigo-700">{fmtMoney(m.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
